import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { setBadge, clearBadge } from "../lib/push";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  // Mirror the unread bell count onto the app-icon badge (PWA installed).
  useEffect(() => {
    if (unread > 0) setBadge(unread); else clearBadge();
  }, [unread]);

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };

  const click = async (n) => {
    await api.patch(`/notifications/${n.id}/read`);
    load();
    setOpen(false);
    if (n.type === "defect_created" || n.type === "customer_report") {
      navigate(`/admin/defects?focus=${n.ref_id}`);
    } else if (n.type === "checklist_assigned") {
      navigate(`/checklist/${n.ref_id}`);
    } else if (n.type === "guest_change") {
      navigate(`/admin/bookings?focus=${n.ref_id}`);
    } else if (n.type === "customer_location") {
      navigate(`/admin/locations?focus=${n.ref_id}`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative p-2 rounded-lg hover:bg-[#1a2a3a] transition-colors"
          aria-label="Varsler"
        >
          <Bell className="w-5 h-5 text-white" />
          {unread > 0 && (
            <span
              data-testid="notif-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-[#BD984D] text-[#0C1824] flex items-center justify-center"
            >
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[calc(100vw-1rem)] max-w-sm sm:w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-[#0C1824]">Varsler</span>
          {unread > 0 && (
            <button
              data-testid="notif-mark-all"
              onClick={markAll}
              className="text-xs flex items-center gap-1 text-[#1DA3B3] hover:underline"
            >
              <CheckCheck className="w-3 h-3" /> Marker alle som lest
            </button>
          )}
        </div>
        <div
          data-testid="notif-list"
          className="max-h-[70vh] overflow-y-auto overscroll-contain"
        >
          {items.length === 0 && (
            <div className="px-4 py-6 text-sm text-[#2B3F4D]">Ingen varsler.</div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              onClick={() => click(n)}
              className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 transition ${
                n.read ? "opacity-60" : ""
              }`}
            >
              <div className="text-sm font-medium text-[#0C1824]">{n.title}</div>
              <div className="text-xs text-[#2B3F4D] mt-0.5">{n.body}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#A57F39] mt-1">
                {new Date(n.created_at).toLocaleString("nb-NO")}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
